import { z } from 'zod';
import {
  type OutputBuildOptions,
  type OutputKind,
  outputBuildOptionsInputSchema,
  outputKindSchema,
  validateOutputOptions,
} from './outputContracts';

const nonBlankText = z.string().refine((value) => value.trim().length > 0, {
  message: 'must_not_be_blank',
});

const count = z.number().int().nonnegative().safe();

export type ApiTimestamp = string | number | null;
export type DeliveryState = 'draft' | 'active' | 'completed' | 'cancelled';
export const customerSyncStatusInputSchema = z
  .object({ status: z.enum(['not_started', 'syncing', 'completed']) })
  .strict();
export type CustomerSyncStatus = z.infer<typeof customerSyncStatusInputSchema>['status'];
export type ClaimMode = 'non_exclusive' | 'range_exclusive' | 'asset_exclusive';
export type RepeatDeliveryPolicy = 'deny_overlap' | 'allow_overlap';
export type EntryState = 'active' | 'voided';
export type DeliveryEntryAssetState = 'active' | 'rejected';
export type DeliveryEntryBuildState = 'pending' | 'building' | 'ready' | 'failed' | 'deleted';
type MaskPolicy = 'none' | 'optional' | 'required';
export type McapOverlayPolicy = 'disabled' | 'preferred' | 'required';
/** @deprecated Prefer `McapOverlayPolicy`: handpose and VIO use the same strict/loose policy. */
export type McapVioPolicy = McapOverlayPolicy;
/** Current creation uses `multiview`; the retired values remain readable for historical deliveries. */
export type McapHandposeProducer = 'none' | 'multiview' | 'mediapipe' | 'gerobot';
/** Current creation uses `basalt`; the retired values remain readable for historical deliveries. */
export type McapVioProducer =
  | 'none'
  | 'basalt'
  | 'okvis2'
  | 'gerobot'
  | 'basalt-stereo'
  | 'basalt-multi6';
export type McapBuildConfigApplyTo = 'newEntriesOnly' | 'allEntries';

interface RenderVariant {
  key: string;
  width?: number;
  height?: number;
  bitrateKbps?: number;
}

interface RenderConfig {
  schemaVersion?: number;
  codec: string;
  pixelFormat: string;
  variants: RenderVariant[];
}

export type BuildConfig =
  | {
      kind: 'video';
      schemaVersion?: number;
      maskPolicy?: MaskPolicy;
      renderConfig?: RenderConfig;
    }
  | {
      kind: 'mcap';
      schemaVersion?: number;
      handpose?: McapHandposeProducer;
      handposePolicy?: McapOverlayPolicy;
      vio?: McapVioProducer;
      includeHandpose?: boolean;
      vioPolicy?: McapVioPolicy;
    };

export type BuildKind = BuildConfig['kind'];

type OutputDescriptor =
  | {
      kind: 'video';
      schemaVersion?: number;
      contentType: string;
      container: string;
      codec: string;
      pixelFormat: string;
      width: number;
      height: number;
      durationMs: number;
      sizeBytes: number;
      bitrateKbps?: number;
      frameRateMilli?: number;
    }
  | {
      kind: 'mcap';
      schemaVersion?: number;
      contentType: string;
      container: string;
      sizeBytes: number;
      messageCount: number;
      chunkCount: number;
      warnings?: Array<
        | 'handpose_not_available'
        | 'handpose_staging_failed'
        | 'handpose_composition_failed'
        | 'vio_not_available'
        | 'vio_staging_failed'
        | 'vio_composition_failed'
      >;
    };

export interface Delivery {
  outputKind: OutputKind;
  outputOptions: OutputBuildOptions;
  deliveryId: string;
  customerId: string;
  name: string;
  description: string | null;
  batchMaxItems: number;
  claimMode: ClaimMode;
  repeatPolicy: RepeatDeliveryPolicy;
  buildConfig: BuildConfig;
  state: DeliveryState;
  customerSyncStatus: CustomerSyncStatus;
  createdBy: string;
  createdAt?: ApiTimestamp;
  updatedAt?: ApiTimestamp;
}

export interface DeliveryCompletionPreview {
  deliveryId: string;
  currentMode: ClaimMode;
  repeatPolicy: RepeatDeliveryPolicy;
  eligible: boolean;
  conflictEntryIds: string[];
}

interface DeliveryMembership {
  deliveryId: string;
  key: string;
  name: string;
  state: DeliveryState;
}

export interface CatalogDeliveryMembership {
  sourceId: string;
  segmentId: string;
  delivery: DeliveryMembership;
  entry: { entryId: string; state: 'active' };
  output: {
    deliveryId: string;
    key: string;
    state: 'pending' | 'blocked' | 'building' | 'ready' | 'failed';
    outputComplete: boolean;
  } | null;
}

export type DeliveryFilterOperator = 'in' | 'not_in';

export interface DeliveryPage {
  items: Delivery[];
  nextCursor?: string | null;
}

export interface DeliveryEntry {
  entryId: string;
  deliveryId: string;
  batchNo: number;
  entryKey?: string;
  segmentId: string;
  segmentKey?: string;
  assetId?: string;
  assetKey?: string;
  filename?: string;
  assetState: DeliveryEntryAssetState;
  durationMs?: number;
  ordinal: number;
  state: EntryState;
  generation: number;
  outputComplete?: boolean;
  buildState?: DeliveryEntryBuildState;
  createdBy: string;
  voidReason: string | null;
  voidedBy: string | null;
  createdAt?: ApiTimestamp;
  updatedAt?: ApiTimestamp;
  voidedAt?: ApiTimestamp;
}

export interface DeliveryEntryOutput {
  entryId: string;
  outputKey: string;
  generation: number;
  payloadUri: string;
  gcsGeneration?: string;
  contentHash: string;
  descriptor: OutputDescriptor;
  producerTaskId: string;
  createdAt?: ApiTimestamp;
  updatedAt?: ApiTimestamp;
}

export interface DeliveryEntryPage {
  items: DeliveryEntry[];
  nextCursor?: string | null;
}

/** `unknown` is an intentional storage-probe result, not a fallback enum member. */
export type ObservedStatus = 'absent' | 'present' | 'unknown';
export type OrphanDisposition = 'leaked' | 'clean' | 'superseded' | 'unknown';

const PURGEABLE_DISPOSITION = 'leaked' as const;

export function isPurgeableOrphan(disposition: OrphanDisposition): boolean {
  return disposition === PURGEABLE_DISPOSITION;
}

export interface ObservedObject {
  status: ObservedStatus;
  generation: string | null;
}

interface DeliveryEntryOutputOrphan {
  entryId: string;
  outputKey: string;
  object: string;
  receiptGeneration: string;
  observed: ObservedObject;
  disposition: OrphanDisposition;
}

export interface DeliveryCascadeGap {
  entryId: string;
  deliveryId: string;
  outputEntryState: string;
  reasonCode: string | null;
}

export interface DeliveryEntryOutputOrphanReport {
  items: DeliveryEntryOutputOrphan[];
  cascadeGaps: DeliveryCascadeGap[];
  complete: boolean;
}

export type TaskState = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

export function isActiveTaskState(state: TaskState): boolean {
  return state === 'queued' || state === 'running';
}

export interface EntryTask {
  taskId: string;
  kind: string;
  state: TaskState;
  attempts: number;
  maxAttempts: number;
  cancelRequested?: boolean;
  reconciled?: boolean;
  error?: string | null;
  errorOutput?: string | null;
  latestFailureReason?: string | null;
}

const claimModeInputSchema = z.enum(['non_exclusive', 'range_exclusive', 'asset_exclusive']);
const repeatPolicyInputSchema = z.enum(['deny_overlap', 'allow_overlap']);
const maskPolicyInputSchema = z.enum(['none', 'optional', 'required']);
const mcapVioPolicyInputSchema = z.enum(['disabled', 'preferred', 'required']);
const mcapHandposeProducerInputSchema = z.enum(['none', 'multiview', 'mediapipe', 'gerobot']);
const mcapVioProducerInputSchema = z.enum([
  'none',
  'basalt',
  'okvis2',
  'gerobot',
  'basalt-stereo',
  'basalt-multi6',
]);
const mcapBuildConfigApplyToSchema = z.enum(['newEntriesOnly', 'allEntries']);
const renderVariantInputSchema = z
  .object({
    key: z.string().min(1),
    width: z.number().int().positive().safe().optional(),
    height: z.number().int().positive().safe().optional(),
    bitrateKbps: z.number().int().positive().safe().optional(),
  })
  .strict();
const renderConfigInputSchema = z
  .object({
    schemaVersion: z.number().optional(),
    codec: z.string().min(1),
    pixelFormat: z.string().min(1),
    variants: z.array(renderVariantInputSchema).min(1),
  })
  .strict();
const buildConfigInputSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('video'),
      schemaVersion: z.number().optional(),
      maskPolicy: maskPolicyInputSchema.optional(),
      renderConfig: renderConfigInputSchema.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('mcap'),
      schemaVersion: z.number().optional(),
      handpose: mcapHandposeProducerInputSchema.optional(),
      handposePolicy: mcapVioPolicyInputSchema.optional(),
      vio: mcapVioProducerInputSchema.optional(),
      includeHandpose: z.boolean().optional(),
      vioPolicy: mcapVioPolicyInputSchema.optional(),
    })
    .strict(),
]);

export const createDeliveryInputSchema = z
  .object({
    deliveryId: z.string().min(1),
    customerId: nonBlankText,
    name: nonBlankText,
    description: nonBlankText.nullable().optional(),
    batchMaxItems: z.number().int().positive().safe(),
    claimMode: claimModeInputSchema,
    repeatPolicy: repeatPolicyInputSchema,
    buildConfig: buildConfigInputSchema,
    outputKind: outputKindSchema,
    outputOptions: outputBuildOptionsInputSchema,
  })
  .strict()
  .superRefine((input, context) => {
    validateOutputOptions(input, context);
    if ((input.outputKind === 'mp4') !== (input.buildConfig.kind === 'video')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['outputKind'],
        message: 'incompatible_output_kind_build_config',
      });
    }
  });

export type CreateDeliveryInput = z.infer<typeof createDeliveryInputSchema>;

export const updateMcapBuildConfigInputSchema = z
  .object({
    buildConfig: z
      .object({
        schemaVersion: z.literal(4),
        kind: z.literal('mcap'),
        handpose: mcapHandposeProducerInputSchema,
        handposePolicy: mcapVioPolicyInputSchema,
        vio: mcapVioProducerInputSchema,
        vioPolicy: mcapVioPolicyInputSchema,
      })
      .strict(),
    applyTo: mcapBuildConfigApplyToSchema,
  })
  .strict();

export type UpdateMcapBuildConfigInput = z.infer<typeof updateMcapBuildConfigInputSchema>;

export const createEntryInputSchema = z
  .object({
    entryId: z.string().min(1),
    segmentId: z.string().min(1),
  })
  .strict();

export type CreateEntryInput = z.infer<typeof createEntryInputSchema>;

export const rebuildEntryInputSchema = z
  .object({
    expectedGeneration: count,
    reason: nonBlankText,
  })
  .strict();

export type RebuildEntryInput = z.infer<typeof rebuildEntryInputSchema>;

export const reasonInputSchema = z.object({ reason: nonBlankText }).strict();

export interface MetadataJsonSchema extends Record<string, unknown> {
  type?: string;
  properties?: Record<string, Record<string, unknown>>;
  required?: string[];
}

type MetadataMapping = Record<string, string>;

export interface DeliveryMetadataBinding {
  bindingId: string;
  deliveryId: string;
  revision: number;
  metadataSchema: MetadataJsonSchema;
  metadataMapping: MetadataMapping;
  createdBy: string;
  createdAt?: ApiTimestamp;
}

const metadataJsonSchemaInputSchema = z
  .object({
    type: z.string().optional(),
    properties: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
    required: z.array(z.string()).optional(),
  })
  .passthrough();
const metadataMappingInputSchema = z.record(z.string(), z.string());

export const putMetadataBindingInputSchema = z
  .object({
    metadataSchema: metadataJsonSchemaInputSchema,
    metadataMapping: metadataMappingInputSchema,
  })
  .strict();

export type PutMetadataBindingInput = z.infer<typeof putMetadataBindingInputSchema>;

export interface MetadataBindingReceipt {
  bindingId: string;
  deliveryId: string;
  revision: number;
  createdBy: string;
}

export type SourceNamespace = 'asset' | 'environment' | 'operator' | 'task' | 'device';

export interface MetadataSource {
  path: string;
  namespace: SourceNamespace;
  leaf: string;
  isAvailable: boolean;
  deliverable: boolean;
  withheldReason?: string;
}

export interface DeliverySummary {
  deliveryId: string;
  state: DeliveryState;
  batches: { total: number };
  entries: { total: number; active: number; voided: number };
  outputs: { completeEntries: number; incompleteEntries: number };
  build?: {
    pending: number;
    building: number;
    ready: number;
    failed: number;
    deleted: number;
  };
  duration?: { totalMs: number; completeMs: number; incompleteMs: number };
}
